import { checkIfTouched } from './helpers'
import { IFormErrors, IFormValues } from './use-form'
import { IProductConstraints, IProductFormState } from '../types/product'
import { MarketplaceProduct } from '../types/marketplace-product'
import {
  validateAmount,
  validateDeliveryDate,
  validateEmail,
  validatePersonaliseCustomMessage,
  validatePersonaliseFromName,
  validatePersonaliseToName,
  validateEmptyField,
} from './validators'
import {
  ProductFields,
  IProductValues,
  IMKPProductValues,
} from '../types/product-form'
import { OptionType } from '../types/product-form-inputs'
import { createNameFromId } from '../components/input-modules'

export enum GiftCardFormDivId {
  CARD_AMOUNT_DIV = 'CARD_AMOUNT_DIV',
  DELIVERY_DETAILS_DIV = 'DELIVERY_DETAILS_DIV',
  RECIPIENT_DIV = 'RECIPIENT_DIV',
  PERSONALIZE_DIV = 'personaliseMessage',
}

export type IProductErrors = IFormErrors<IProductValues>
export type IMKPProductErrors = IFormErrors<IMKPProductValues>

export const scrollToErrorMappings = {
  [ProductFields.AMOUNT]: GiftCardFormDivId.CARD_AMOUNT_DIV,
  [ProductFields.PERSONALISE_TO_NAME]: GiftCardFormDivId.RECIPIENT_DIV,
  [ProductFields.PERSONALISE_FROM_NAME]: GiftCardFormDivId.RECIPIENT_DIV,
  [ProductFields.PERSONALISE_MESSAGE]: GiftCardFormDivId.PERSONALIZE_DIV,
  [ProductFields.DELIVERY_DATE]: GiftCardFormDivId.DELIVERY_DETAILS_DIV,
  [ProductFields.DELIVERY_METHOD]: GiftCardFormDivId.DELIVERY_DETAILS_DIV,
  [ProductFields.DELIVERY_METHOD_EMAIL]: GiftCardFormDivId.DELIVERY_DETAILS_DIV,
}

export const DGCProductValidate =
  (options: IProductConstraints, state: IProductFormState) =>
  (
    values: IFormValues<IProductValues>,
    isSubmitting?: boolean
  ): IProductErrors => {
    const errors: IProductErrors = {}

    const isTouched = checkIfTouched<IProductValues>(values, isSubmitting)

    if (isTouched(ProductFields.AMOUNT)) {
      errors.amount = validateAmount(
        options.minValue,
        options.maxValue,
        values.amount
      )
    }

    if (state.recipientExpanded) {
      if (isTouched(ProductFields.PERSONALISE_TO_NAME)) {
        errors.personaliseToName = validatePersonaliseToName(
          values.personaliseToName
        )
      }

      if (isTouched(ProductFields.PERSONALISE_FROM_NAME)) {
        errors.personaliseFromName = validatePersonaliseFromName(
          values.personaliseFromName
        )
      }

      if (isTouched(ProductFields.PERSONALISE_MESSAGE)) {
        errors.personaliseMessage = validatePersonaliseCustomMessage(
          values.personaliseMessage
        )
      }
    }

    if (isTouched(ProductFields.DELIVERY_METHOD_EMAIL)) {
      errors.deliveryMethodEmail = validateEmail(values.deliveryMethodEmail)
    }

    if (isTouched(ProductFields.DELIVERY_DATE)) {
      errors.deliveryDate = validateDeliveryDate(
        options.maxDeliveryDateMonths,
        values.deliveryDate
      )
    }

    return errors
  }

export const MKPProductValidate =
  (product: MarketplaceProduct) =>
  (
    values: IFormValues<IMKPProductValues>,
    isSubmitting?: boolean
  ): IMKPProductErrors => {
    const errors: IMKPProductErrors = {}

    const isTouched = checkIfTouched<IMKPProductValues>(values, isSubmitting)
    const mandatoryOptions = product.options.map(o => {
      return {
        fieldName: createNameFromId(o.id, OptionType.OPTION),
        label: o.displayName,
        type: o.type,
      }
    })

    //TODO: Validate quantity based on stock, number of items already in cart, etc...?

    mandatoryOptions.forEach(m => {
      if (isTouched(m.fieldName)) {
        errors[m.fieldName] = validateEmptyField(
          values[m.fieldName]?.toString(),
          m.label,
          m.type
        )
      }
    })

    return errors
  }
